import {
  ArrowRightIcon,
  ArrowRightStartOnRectangleIcon,
  BoltIcon,
  BuildingStorefrontIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  HomeIcon,
  LinkIcon,
  LockClosedIcon,
  QuestionMarkCircleIcon,
  ShoppingCartIcon,
  SparklesIcon,
  UserCircleIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { features } from '@sparklayer-io/spark-dash-core/constants/features';
import {
  isLive as isLiveEnvironment,
  isShopify,
} from '@sparklayer-io/spark-dash-core/constants/platform';
import type { Environment } from '@sparklayer-io/spark-dash-core/context/env-context';
import {
  platformStorefrontUrl,
  shopifyAppUrl,
  wixAppUrl,
} from '@sparklayer-io/spark-dash-core/lib/platform-links';
import { useColorScheme } from '@sparklayer-io/spark-dash-ui/hooks/use-color-scheme';
import { useEventListener } from '@sparklayer-io/spark-dash-ui/hooks/use-event-listener';
import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { Toggle } from '@sparklayer-io/spark-dash-ui/spark';
import { useLocation, useParams, useRouter } from '@tanstack/react-router';
import { t } from 'i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHostSession } from '@/components/page-host';
import { setCurrentSite } from '@/lib/handlers/api';
import { loadSessionEnv } from '@/lib/session';
import { I18N_SIDEBAR } from './constants';
import {
  EXPANDED_NAV_STORAGE_KEY,
  filterNavItems,
  hasPermission,
  isNavItemActive,
  isPlatform,
  isSubNavItemActive,
  type NavItem,
  type RuntimeNavigate,
  type SubNavItem,
} from './helpers';
import { NavDisclosureButton, NavLink, NavSubMenu, SubNavLink } from './nav-items';
import { ShopifyIcon, WixIcon } from './platform-icons';
import {
  SidebarActionLink,
  UserMenuButton,
  UserMenuLink,
  UserMenuThemeSelector,
} from './sidebar-links';
import { SiteSwitcher } from './site-switcher';

export type SidebarProps = {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
};

export function Sidebar({ sidebarOpen, onToggleSidebar }: SidebarProps) {
  const router = useRouter();
  const { pathname, search, hash } = useLocation();
  const params = useParams({ strict: false }) as { siteId?: string; siteEnv?: string };
  const { siteId, env, user } = useHostSession();

  const isSparkStaff = !!user?.user.is_spark_staff;
  const { preference: colorScheme, setPreference: setColorScheme } = useColorScheme();

  const navigateTo = router.navigate as unknown as RuntimeNavigate;

  const userMenuContainerRef = useRef<HTMLDivElement>(null);

  const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
  const [pendingEnv, setPendingEnv] = useState<Environment | null>(null);
  const [expandedNavItems, setExpandedNavItems] = useState<Record<string, boolean>>(() => {
    try {
      const saved = sessionStorage.getItem(EXPANDED_NAV_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(EXPANDED_NAV_STORAGE_KEY, JSON.stringify(expandedNavItems));
    } catch {}
  }, [expandedNavItems]);

  const coreNav = useMemo<NavItem[]>(() => {
    if (!env) return [];
    const prefix = `/${env.team.slugged_name}/${env.environment}`;
    const platform = env.platform ?? env.connected_platform;
    const nav: NavItem[] = [];
    if (env.team.is_partner) {
      nav.push({
        href: `/${env.team.slugged_name}/partners`,
        title: t(`${I18N_SIDEBAR}.nav.partner_dashboard`),
        icon: UserCircleIcon,
      });
    }
    nav.push(
      { href: `${prefix}/`, title: t(`${I18N_SIDEBAR}.nav.home`), icon: HomeIcon },
      {
        href: `${prefix}/activity`,
        title: t(`${I18N_SIDEBAR}.nav.activity`),
        icon: ShoppingCartIcon,
      },
      {
        href: `${prefix}/pricing`,
        title: t(`${I18N_SIDEBAR}.nav.pricing`),
        icon: CurrencyDollarIcon,
        subNav: [
          { href: `${prefix}/pricing/lists`, title: t(`${I18N_SIDEBAR}.nav.price_lists`) },
          { href: `${prefix}/pricing/editor`, title: t(`${I18N_SIDEBAR}.nav.price_editor`) },
          { href: `${prefix}/pricing/discounts`, title: t(`${I18N_SIDEBAR}.nav.discounts`) },
        ],
      },
      {
        href: `${prefix}/customers`,
        title: t(`${I18N_SIDEBAR}.nav.customers`),
        icon: UsersIcon,
        subNav: [
          { href: `${prefix}/customers/list`, title: t(`${I18N_SIDEBAR}.nav.customers`) },
          {
            href: `${prefix}/customers/companies`,
            title: t(`${I18N_SIDEBAR}.nav.companies`),
            feature: features.COMPANIES,
          },
          { href: `${prefix}/customers/agents`, title: t(`${I18N_SIDEBAR}.nav.agents`) },
          { href: `${prefix}/customers/groups`, title: t(`${I18N_SIDEBAR}.nav.groups`) },
          { href: `${prefix}/customers/roles`, title: t(`${I18N_SIDEBAR}.nav.roles`) },
        ],
      },
      {
        href: `${prefix}/forms`,
        title: t(`${I18N_SIDEBAR}.nav.forms`),
        icon: DocumentTextIcon,
        subNav: [
          { href: `${prefix}/forms`, title: t(`${I18N_SIDEBAR}.nav.forms`) },
          { href: `${prefix}/forms/tables`, title: t(`${I18N_SIDEBAR}.nav.data_tables`) },
        ],
      },
      {
        href: `${prefix}/integrations`,
        title: t(`${I18N_SIDEBAR}.nav.integrations`),
        icon: LinkIcon,
        subNav: [
          { href: `${prefix}/integrations/platform`, title: t(`${I18N_SIDEBAR}.nav.platform`) },
          { href: `${prefix}/integrations/partners`, title: t(`${I18N_SIDEBAR}.nav.partners`) },
          {
            href: `${prefix}/integrations/products-sync`,
            title: t(`${I18N_SIDEBAR}.nav.product_sync`),
          },
        ],
      },
      {
        href: `${prefix}/storefront`,
        title: t(`${I18N_SIDEBAR}.nav.storefront`),
        icon: BuildingStorefrontIcon,
        subNav: [
          { href: `${prefix}/storefront/widgets`, title: t(`${I18N_SIDEBAR}.nav.widgets`) },
          { href: `${prefix}/storefront/options`, title: t(`${I18N_SIDEBAR}.nav.options`) },
        ],
      },
      {
        href: `${prefix}/configuration`,
        title: t(`${I18N_SIDEBAR}.nav.settings`),
        icon: Cog6ToothIcon,
        subNav: [
          {
            href: `${prefix}/configuration/onboarding`,
            title: t(`${I18N_SIDEBAR}.nav.onboarding`),
            hidden: isShopify(platform),
          },
          {
            href: `${prefix}/configuration/general`,
            title: t(`${I18N_SIDEBAR}.nav.configurations`),
          },
          { href: `${prefix}/configuration/shipping`, title: t(`${I18N_SIDEBAR}.nav.shipping`) },
          { href: `${prefix}/configuration/api`, title: t(`${I18N_SIDEBAR}.nav.api`) },
          {
            href: `${prefix}/configuration/theme`,
            title: t(`${I18N_SIDEBAR}.nav.templates`),
            feature: features.THEMES,
          },
        ],
      },
    );

    return filterNavItems(nav, env.team.slugged_name, platform, isSparkStaff).map((item) => ({
      ...item,
      subNav: item.subNav
        ? filterNavItems(item.subNav, env.team.slugged_name, platform, isSparkStaff)
        : undefined,
    }));
  }, [env, isSparkStaff]);

  const accountNav = useMemo<NavItem[]>(() => {
    if (!env || !user) return [];
    const prefix = `/${env.team.slugged_name}`;
    const platform = env.platform ?? env.connected_platform;
    const nav: NavItem[] = [
      {
        href: `${prefix}/settings/account`,
        title: t(`${I18N_SIDEBAR}.account_nav.account`),
        icon: UserCircleIcon,
      },
      {
        href: `${prefix}/settings/password`,
        title: t(`${I18N_SIDEBAR}.account_nav.password`),
        icon: LockClosedIcon,
      },
      {
        href: `${prefix}/settings/plan`,
        title: t(`${I18N_SIDEBAR}.account_nav.plan`),
        icon: CreditCardIcon,
        hide: env.team.is_partner || !hasPermission(user, 'updateBilling'),
      },
      {
        href: `${prefix}/settings/team`,
        title: t(`${I18N_SIDEBAR}.account_nav.team`),
        icon: UsersIcon,
      },
    ];
    return filterNavItems(nav, env.team.slugged_name, platform, isSparkStaff);
  }, [env, user, isSparkStaff]);

  const staffNav = useMemo<SubNavItem[]>(() => {
    if (!env) return [];
    const prefix = `/${env.team.slugged_name}/staff`;
    const platform = env.platform ?? env.connected_platform;
    return filterNavItems(
      [
        { href: `${prefix}/manage-billing`, title: t(`${I18N_SIDEBAR}.staff_nav.manage_billing`) },
        { href: `${prefix}/manage-team`, title: t(`${I18N_SIDEBAR}.staff_nav.manage_team`) },
        { href: `${prefix}/customer-sync`, title: t(`${I18N_SIDEBAR}.staff_nav.customer_sync`) },
      ],
      env.team.slugged_name,
      platform,
      isSparkStaff,
    );
  }, [env, isSparkStaff]);

  const teamWebsiteUrl = useMemo(() => {
    const website = env?.team.website?.trim();
    if (!website) return null;
    return /^https?:\/\//.test(website) ? website : `https://${website}`;
  }, [env]);

  const storefrontUrl = useMemo(
    () =>
      platformStorefrontUrl(env?.platform ?? env?.connected_platform, env?.store_id) ??
      teamWebsiteUrl,
    [env, teamWebsiteUrl],
  );

  const showEnvironmentSelector =
    !!env &&
    (env.team.is_partner ||
      (env.billing.plan_key !== 'development' && !!env.billing.is_advanced_features_enabled));
  const showingLive = pendingEnv
    ? pendingEnv === 'live'
    : !!env && isLiveEnvironment(env.environment);

  const showPlanLink =
    !!env &&
    !env.team.is_partner &&
    (env.billing.has_pending_plan ||
      (env.billing.has_active_plan === false && env.billing.allow_change_plan === true) ||
      (env.billing.trial_ends_at != null && env.billing.has_trial_expired === false));

  const planLinkText = (() => {
    if (!env) return '';
    if (env.billing.has_pending_plan) return t(`${I18N_SIDEBAR}.plan_link.confirm_plan`);
    if (env.billing.has_active_plan === false || env.billing.has_trial_expired === true) {
      return t(`${I18N_SIDEBAR}.plan_link.select_plan`);
    }
    return t(`${I18N_SIDEBAR}.plan_link.trial_days_left`, { days: env.billing.trial_ends_in_days });
  })();

  // Expand nav items whose subNav is active, without clobbering an explicit collapse the user made.
  useEffect(() => {
    setExpandedNavItems((prev) => {
      let next = prev;
      for (const item of coreNav) {
        if (
          item.subNav?.length &&
          isNavItemActive(pathname, item) &&
          next[item.href] === undefined
        ) {
          next = { ...next, [item.href]: true };
        }
      }
      if (
        env &&
        isSparkStaff &&
        pathname.startsWith(`/${env.team.slugged_name}/staff`) &&
        next.staff === undefined
      ) {
        next = { ...next, staff: true };
      }
      return next;
    });
  }, [pathname, coreNav, env, isSparkStaff]);

  useEventListener(
    document,
    'click',
    (event) => {
      const target = event.target as Node | null;
      if (!(target && userMenuContainerRef.current?.contains(target))) {
        setUserMenuOpen(false);
      }
    },
    userMenuOpen,
  );

  function toggleNavItem(href: string) {
    setExpandedNavItems((prev) => ({ ...prev, [href]: !prev[href] }));
  }

  async function switchEnvironment() {
    if (!env) return;
    const newEnv: Environment = isLiveEnvironment(env.environment) ? 'test' : 'live';
    setPendingEnv(newEnv);

    try {
      // Routes naming an environment carry it in the url, so move the url and let the guard load
      // it. The rest (e.g. `/:siteId/settings`) have nowhere to put it, so load it here instead.
      if (params.siteEnv && params.siteId) {
        const newPathname = pathname.replace(
          `/${params.siteId}/${params.siteEnv}`,
          `/${params.siteId}/${newEnv}`,
        );
        await navigateTo({ to: newPathname, search, hash });
      } else if (siteId) {
        await loadSessionEnv(siteId, newEnv, 'partner');
        setCurrentSite({ siteId, siteEnv: newEnv });
      }
    } finally {
      setPendingEnv(null);
    }
  }

  function handleNavClick(event: React.MouseEvent, href: string) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)
      return;
    event.preventDefault();
    void navigateTo({ to: href });
  }

  if (!env || !user) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-40 flex h-full lg:relative lg:z-0 lg:shrink-0',
        !sidebarOpen && 'hidden lg:flex',
      )}
    >
      {/* Mobile menu gray background overlay*/}
      <div className="fixed inset-0 bg-spark-overlay-backdrop lg:hidden" aria-hidden="true" />

      <div className="relative flex w-full max-w-xs flex-1 flex-col border-spark-border-subtle bg-spark-surface-subtle pt-3 lg:w-64 lg:overflow-hidden lg:border-r">
        {/* Mobile menu close button */}
        <button
          type="button"
          className="absolute inset-e-0 top-0 -mr-12 ml-1 flex size-10 cursor-pointer items-center justify-center rounded-full pt-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-spark-text-inverse focus-visible:ring-inset lg:hidden"
          onClick={onToggleSidebar}
        >
          <span className="sr-only">{t(`${I18N_SIDEBAR}.close`)}</span>
          <XMarkIcon className="size-6 text-spark-text-inverse" />
        </button>

        {/* Nav items */}
        <div className="relative flex h-full flex-col">
          <SiteSwitcher env={env} user={user} siteId={siteId} />

          {/* Scrollable middle */}
          <div className="flex-1 overflow-y-auto pb-4">
            <nav className="space-y-4 px-4">
              <div className="relative pt-1 text-spark-text-secondary">
                {coreNav.map((coreNavItem) => {
                  const active = isNavItemActive(pathname, coreNavItem);
                  const hasSubNav = !!coreNavItem.subNav?.length;
                  const expanded = !!expandedNavItems[coreNavItem.href];
                  return (
                    <div key={coreNavItem.href}>
                      {hasSubNav ? (
                        <NavDisclosureButton
                          icon={coreNavItem.icon}
                          title={coreNavItem.title}
                          active={active && !expanded}
                          expanded={expanded}
                          onClick={() => toggleNavItem(coreNavItem.href)}
                        />
                      ) : (
                        <NavLink
                          href={coreNavItem.href}
                          title={coreNavItem.title}
                          icon={coreNavItem.icon}
                          active={active}
                          onNavigate={handleNavClick}
                        />
                      )}
                      {hasSubNav && (
                        <NavSubMenu expanded={expanded}>
                          {coreNavItem.subNav
                            ?.filter((i) => !i.hidden)
                            .map((subNavItem) => (
                              <SubNavLink
                                key={subNavItem.href}
                                item={subNavItem}
                                active={isSubNavItemActive(
                                  pathname,
                                  subNavItem,
                                  coreNavItem.subNav ?? [],
                                )}
                                onNavigate={handleNavClick}
                              />
                            ))}
                        </NavSubMenu>
                      )}
                    </div>
                  );
                })}
              </div>

              <Divider />

              <div className="text-spark-text-secondary">
                {isSparkStaff && (
                  <div>
                    <NavDisclosureButton
                      icon={BoltIcon}
                      title={t(`${I18N_SIDEBAR}.staff_nav.staff`)}
                      active={
                        pathname.startsWith(`/${env.team.slugged_name}/staff`) &&
                        !expandedNavItems.staff
                      }
                      expanded={!!expandedNavItems.staff}
                      onClick={() => toggleNavItem('staff')}
                    />
                    <NavSubMenu expanded={!!expandedNavItems.staff}>
                      {staffNav.map((subNavItem) => (
                        <SubNavLink
                          key={subNavItem.href}
                          item={subNavItem}
                          active={isSubNavItemActive(pathname, subNavItem, staffNav)}
                          onNavigate={handleNavClick}
                        />
                      ))}
                    </NavSubMenu>
                  </div>
                )}
                <SidebarActionLink
                  href="/help"
                  label={t(`${I18N_SIDEBAR}.actions.help`)}
                  icon={QuestionMarkCircleIcon}
                  active={pathname === '/help'}
                  onNavigate={handleNavClick}
                />
                <SidebarActionLink
                  href={`/${env.team.slugged_name}/${env.environment}/features`}
                  label={t(`${I18N_SIDEBAR}.actions.explore_features`)}
                  icon={SparklesIcon}
                  active={pathname === `/${env.team.slugged_name}/${env.environment}/features`}
                  onNavigate={handleNavClick}
                />
                {isPlatform(env, 'shopify') && (
                  <SidebarActionLink
                    href={shopifyAppUrl(env.store_id ?? '')}
                    label={t(`${I18N_SIDEBAR}.actions.return_to_shopify`)}
                    iconNode={
                      <ShopifyIcon className="mr-2 size-4 fill-spark-text-secondary group-hover:fill-spark-primary-accent" />
                    }
                    external
                  />
                )}
                {!isPlatform(env, 'shopify') && isPlatform(env, 'wix') && (
                  <SidebarActionLink
                    href={wixAppUrl(env.store_id ?? '')}
                    label={t(`${I18N_SIDEBAR}.actions.return_to_wix`)}
                    iconNode={
                      <WixIcon className="mr-2 size-4 fill-spark-text-secondary group-hover:fill-spark-primary-accent" />
                    }
                    external
                  />
                )}
                {isSparkStaff && storefrontUrl && (
                  <SidebarActionLink
                    href={storefrontUrl}
                    label={t(`${I18N_SIDEBAR}.actions.go_to_storefront`)}
                    icon={BuildingStorefrontIcon}
                    external
                  />
                )}
              </div>

              {showEnvironmentSelector && (
                <>
                  <Divider />

                  <div className="flex items-center rounded-lg px-2 py-1.5">
                    <Toggle
                      checked={!showingLive}
                      disabled={env.team.is_partner}
                      onChange={() => void switchEnvironment()}
                    />
                    <span className="ml-2 font-medium text-sm text-spark-text-secondary">
                      {t(`${I18N_SIDEBAR}.test_mode`)}
                    </span>
                  </div>
                </>
              )}
            </nav>
          </div>

          {showPlanLink && (
            <a
              href={`/${env.team.slugged_name}/settings/plan`}
              onClick={(event) => handleNavClick(event, `/${env.team.slugged_name}/settings/plan`)}
              className="m-6 inline-flex items-center justify-center rounded border border-spark-border-subtle bg-spark-primary-soft px-2 py-1 text-spark-primary-accent text-xs"
            >
              {planLinkText}
              <button
                type="button"
                className="ml-0.5 inline-flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full text-spark-primary-accent/70"
              >
                <ArrowRightIcon className="size-4" aria-hidden="true" />
              </button>
            </a>
          )}

          <div ref={userMenuContainerRef} className="relative mx-4 mb-4">
            {userMenuOpen && (
              <div
                className="absolute inset-x-0 bottom-full z-10 mb-1 rounded-lg border border-spark-border-subtle bg-spark-surface shadow-lg"
                role="menu"
              >
                <div className="border-spark-border-subtle border-b p-2">
                  <UserMenuThemeSelector value={colorScheme} onChange={setColorScheme} />
                </div>

                <div className="p-1">
                  {accountNav.map((item) => (
                    <UserMenuLink
                      key={item.href}
                      href={item.href}
                      label={item.title}
                      icon={item.icon}
                      active={isNavItemActive(pathname, item)}
                      onClick={(event) => {
                        handleNavClick(event, item.href);
                        setUserMenuOpen(false);
                      }}
                    />
                  ))}
                  <UserMenuLink
                    href="/logout"
                    label={t(`${I18N_SIDEBAR}.account_nav.sign_out`)}
                    icon={ArrowRightStartOnRectangleIcon}
                    onClick={(event) => {
                      handleNavClick(event, '/logout');
                      setUserMenuOpen(false);
                    }}
                  />
                </div>
              </div>
            )}

            <UserMenuButton
              name={user.user.name}
              email={user.user.email}
              onClick={() => setUserMenuOpen((open) => !open)}
            />
          </div>
        </div>
      </div>

      {/* Dummy element to force sidebar to shrink to fit close icon */}
      <div className="w-14 shrink-0 lg:hidden" aria-hidden="true" />
    </div>
  );
}

function Divider() {
  return <div className="border-spark-border-subtle border-t" />;
}
